import { CARD_STYLE_SIMPLE } from '@eventbrite/eds-card';
import { CardList } from '@eventbrite/eds-card-list';
import React, { FunctionComponent } from 'react';
import { TicketsFormat } from '../../../constants/statsig';
import { isMultipleVariantType } from '../../../containers/ticketSelection/utils/ticketUtils';
import { getTicketsAsIndividualCardsExperimentVariant } from '../../../utils/statsig';
import DisplayContentWithMultipleInputVariants, {
    DisplayContentWithMultipleInputVariantsProps,
} from '../DisplayContentWithMultipleInputVariants';
import ConnectedTicketDisplayCardContentSwitcher, {
    ConnectedTicketDisplayCardContentSwitcherProps,
} from '../TicketDisplayCardContentSwitcher';

export interface TicketsSelectionListProps
    extends Pick<
            DisplayContentWithMultipleInputVariantsProps,
            'onQuantityChange' | 'onJoinWaitlist'
        >,
        Pick<
            ConnectedTicketDisplayCardContentSwitcherProps,
            'isFormSubmitting' | 'ticketReseller' | 'onDonationChange'
        > {
    shouldHideStatusOnTickets: ConnectedTicketDisplayCardContentSwitcherProps['shouldHideStatus'];
    tickets: Array<
        Pick<
            DisplayContentWithMultipleInputVariantsProps,
            | 'id'
            | 'currencyFormat'
            | 'description'
            | 'displayPrice'
            | 'hasPrimaryVariant'
            | 'hideSaleDates'
            | 'initiallyExpanded'
            | 'name'
            | 'priceRange'
            | 'salesEnd'
            | 'salesStart'
            | 'shouldHideStatus'
            | 'showJoinWaitlist'
            | 'statusKey'
            | 'variants'
            | 'feeAndTax'
            | 'image'
            | 'isDisabled'
            | 'maximumQuantityPerOrder'
            | 'onToggle'
            | 'quantityRemaining'
            | 'selectedQuantity'
        > &
            Pick<
                ConnectedTicketDisplayCardContentSwitcherProps,
                | 'category'
                | 'currencyFormat'
                | 'currencySymbol'
                | 'description'
                | 'displayPrice'
                | 'donationAmount'
                | 'feeAndTax'
                | 'hideSaleDates'
                | 'hideSelect'
                | 'id'
                | 'image'
                | 'includeFee'
                | 'includeTax'
                | 'isCurrencySuffix'
                | 'isDisabled'
                | 'isTicketTiered'
                | 'name'
                | 'paymentConstraints'
                | 'primaryPrice'
                | 'quantityRemaining'
                | 'salesEnd'
                | 'salesStart'
                | 'selectedQuantity'
                | 'shouldHideStatus'
                | 'showJoinWaitlist'
                | 'statusKey'
                | 'ticketOptionsRange'
                | 'variantId'
                | 'variants'
            > & { variantInputType?: string | null | undefined }
    >;
}

export const TicketsSelectionList: FunctionComponent<
    TicketsSelectionListProps
> = ({
    tickets,
    onQuantityChange,
    onDonationChange,
    onJoinWaitlist,
    ticketReseller,
    isFormSubmitting,
}) => {
    const items = tickets.map((ticket) => {
        const hasMultipleInputVariants = isMultipleVariantType(ticket);

        const content = hasMultipleInputVariants ? (
            <DisplayContentWithMultipleInputVariants
                key={ticket.id}
                onQuantityChange={onQuantityChange}
                onDonationChange={onDonationChange}
                onJoinWaitlist={onJoinWaitlist}
                isFormSubmitting={isFormSubmitting}
                currencyFormat={ticket.currencyFormat}
                description={ticket.description}
                displayPrice={ticket.displayPrice}
                hasPrimaryVariant={ticket.hasPrimaryVariant}
                hideSaleDates={ticket.hideSaleDates}
                id={ticket.id}
                initiallyExpanded={ticket.initiallyExpanded}
                name={ticket.name}
                priceRange={ticket.priceRange}
                salesEnd={ticket.salesEnd}
                salesStart={ticket.salesStart}
                shouldHideStatus={ticket.shouldHideStatus}
                showJoinWaitlist={ticket.showJoinWaitlist}
                statusKey={ticket.statusKey}
                variants={ticket.variants}
                feeAndTax={ticket.feeAndTax}
                image={ticket.image}
                isDisabled={ticket.isDisabled}
                maximumQuantityPerOrder={ticket.maximumQuantityPerOrder}
                onToggle={ticket.onToggle}
                quantityRemaining={ticket.quantityRemaining}
                selectedQuantity={ticket.selectedQuantity}
            />
        ) : (
            <ConnectedTicketDisplayCardContentSwitcher
                key={ticket.id}
                onQuantityChange={onQuantityChange}
                onDonationChange={onDonationChange}
                onJoinWaitlist={onJoinWaitlist}
                ticketReseller={ticketReseller}
                isFormSubmitting={isFormSubmitting}
                category={ticket.category}
                currencyFormat={ticket.currencyFormat}
                currencySymbol={ticket.currencySymbol}
                description={ticket.description}
                displayPrice={ticket.displayPrice}
                donationAmount={ticket.donationAmount}
                feeAndTax={ticket.feeAndTax}
                hideSaleDates={ticket.hideSaleDates}
                hideSelect={ticket.hideSelect}
                id={ticket.id}
                image={ticket.image}
                includeFee={ticket.includeFee}
                includeTax={ticket.includeTax}
                isCurrencySuffix={ticket.isCurrencySuffix}
                isDisabled={ticket.isDisabled}
                isTicketTiered={ticket.isTicketTiered}
                name={ticket.name}
                paymentConstraints={ticket.paymentConstraints}
                primaryPrice={ticket.primaryPrice}
                quantityRemaining={ticket.quantityRemaining}
                salesEnd={ticket.salesEnd}
                salesStart={ticket.salesStart}
                selectedQuantity={ticket.selectedQuantity}
                shouldHideStatus={ticket.shouldHideStatus}
                showJoinWaitlist={ticket.showJoinWaitlist}
                statusKey={ticket.statusKey}
                ticketOptionsRange={ticket.ticketOptionsRange}
                variantId={ticket.variantId}
                variants={ticket.variants}
            />
        );

        return {
            id: ticket.id,
            content,
            style:
                !hasMultipleInputVariants &&
                getTicketsAsIndividualCardsExperimentVariant() ===
                    TicketsFormat.card
                    ? CARD_STYLE_SIMPLE
                    : null,
        };
    });

    return (
        <div
            className="tickets-container"
            data-automation="tickets-container"
            data-spec="tickets-selection-list-container"
        >
            <CardList items={items} />
        </div>
    );
};

export default TicketsSelectionList;
